<template>
  <div
    class="d-flex flex-wrap schedule-board-filters mb-xs-2 mb-md-2 gap-3 !items-end"
  >
    <Select
      v-model="department_ids"
      data-cy="departmentSelector"
      multiple
      :options="departmentOptions"
      label="Department"
      placeholder="All Departments "
      class="department-selector"
    >
      <template #item-row="{ option }">
        <div class="option-row">
          <span :data-cy="`departmentName-${option.label}`">{{
            option.label
          }}</span>
        </div>
      </template>
    </Select>
    <div class="d-flex">
      <Datepicker
        v-model="start"
        placeholder="Date"
        class="schedule-date-picker"
        data-cy="schedule-board-datepicker"
        buttons
      />
      <div
        class="today-btn-cont"
        data-cy="today-btn"
        @click="handleDateChange(null)"
      >
        Today
      </div>
    </div>
    <CalendarEventModal
      v-if="eventModalOpen"
      :calendar-event="calendarEvent"
      @close="closeEventModal"
    ></CalendarEventModal>
    <CheckAvailabilityModal
      class="availability-btn hidden md:block"
    ></CheckAvailabilityModal>
    <CreateEventButton data-cy="new-event" />
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import Button from "@/components/Button.vue";
import Datepicker from "@/components/Datepicker.vue";
import CONSTANTS from "@/Constants.js";
import CalendarEventModal from "@/Scheduler/CalendarEventModal.vue";
import CheckAvailabilityModal from "@/Scheduler/CheckAvailabilityModal.vue";
import CreateEventButton from "@/SchedulerV2/CreateEventButton.vue";
import { CalendarEvent } from "@/typings/calendarEvent";

const { DATE_YEAR_CAL_FORMAT } = CONSTANTS;

export default {
  components: {
    CalendarEventModal,
    CheckAvailabilityModal,
    CreateEventButton,
    Datepicker,
    // eslint-disable-next-line
    Button
  },
  data: function () {
    return {
      calendarEvent: new CalendarEvent(),
      eventModalOpen: false
    };
  },
  computed: {
    ...mapState({
      scheduleBoardFilters: "scheduleBoardFilters",
      departmentOptions({ departments }) {
        return departments
          .filter((dept) => !dept.archived)
          .map((dept) => ({
            label: dept.name,
            value: dept.id
          }));
      }
    }),
    department_ids: {
      get() {
        return this.scheduleBoardFilters.department_ids;
      },
      set(val) {
        const departmentIds = val ? val.join(",").trim() : null;
        updateQueryParam("department_ids", departmentIds, true);
        this.setScheduleBoardFilters({ department_ids: val });
      }
    },
    start: {
      get() {
        return this.scheduleBoardFilters.start;
      },
      set(val) {
        this.handleDateChange(val);
      }
    },
    isToday() {
      return moment.tz().isSame(this.start, "day");
    }
  },
  created() {
    this.setDefaultFilters();
  },
  mounted() {
    EventBus.$on("edit-calendar-event", this.editCalendarEvent);
  },
  beforeUnmount() {
    EventBus.$off("edit-calendar-event", this.editCalendarEvent);
  },
  methods: {
    ...mapActions(["setScheduleBoardFilters"]),
    handleDateChange(val) {
      const date = val || new Date();
      this.updateDateText(date);
      this.setScheduleBoardFilters({ start: date });
    },
    updateDateText(date) {
      const newDate = moment(date);
      updateQueryParam("start", newDate.format(DATE_YEAR_CAL_FORMAT), true);
      $(".schedule-board-date").text(`${newDate.format("ddd MMM Do, YYYY")}`);
      // If global calendar instance is available
      // immediately go to new date
      // setTimeout puts the calendar render on a separate call stack
      // so the UI date change can render quicker
      setTimeout(() => {
        if (window._FC) {
          window._FC.gotoDate(date);
        } else {
          EventBus.$emit("schedule-board-date-change", newDate);
        }
      });
    },
    setDefaultFilters() {
      const { start, department_ids } = getQueryVariables();
      // Pull cached filters
      let cachedFilters = localStorage.getItem("scheduleBoardFilters");
      cachedFilters = cachedFilters ? JSON.parse(cachedFilters) : {};
      // Prefer query params over cached filters
      const deptIds =
        department_ids || cachedFilters.department_ids
          ? cachedFilters.department_ids.join(",")
          : null;
      const departmentsFilter = deptIds ? deptIds.split(",") : null;
      const filters = {
        department_ids: departmentsFilter,
        start: start || cachedFilters.start || new Date()
      };
      this.setScheduleBoardFilters(filters);
    },
    editCalendarEvent(event) {
      this.calendarEvent = new CalendarEvent(event);
      this.eventModalOpen = true;
    },
    closeEventModal() {
      this.eventModalOpen = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.department-selector {
  width: 250px;
}
.schedule-date-picker {
  width: 180px !important;
  margin-bottom: 0 !important;
}
.today-btn-cont {
  border: 1.5px solid var(--sera-color-fields-border, #9ca4aa);
  border-left: none;
  border-radius: 0 4px 4px 0;
  color: var(--text-links);
  height: 36px;
  align-self: end;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  top: 1px;
  position: relative;
  cursor: pointer;
  &:hover {
    color: #fff;
    background-color: var(--primary-color);
  }
}
:deep(.event-button) {
  padding: 0.625rem 1.25rem !important;
  font-weight: 400 !important;
  background-color: var(--sera-color-text-links) !important;
  border: 1.5px solid var(--sera-color-fields-border, #9ca4aa);
  &:hover {
    background-color: rgb(30, 66, 159) !important; /* bg-blue-800 */
    border-color: rgb(30, 66, 159) !important; /* bg-blue-800 */
  }
}

:deep([data-cy="next-day-btn"]) {
  border-radius: 0 !important;
}
:deep(.option) {
  white-space: nowrap;
}

:deep(.select-box) {
  box-shadow: none !important;
}

:deep(.arrow-cont) {
  box-shadow: none !important;
}

:deep(.mx-input-wrapper .form-control) {
  box-shadow: none !important;
  height: 36px !important;
}
</style>
